/**
 * The badge next to a control that is switched off because of the plan.
 *
 * Panels stay reachable and controls stay visible: a disabled control with no
 * explanation reads as broken, so the badge names the tier that would unlock
 * it and the tooltip says why. Clicking the badge goes to the account page —
 * the badge, not the control, because a disabled control receives no events.
 *
 * No prices, no upsell copy: the badge states a fact and gets out of the way.
 */

import { Tag, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import type { PlanTierV3 } from "@/types";

const TIER_LABELS: Record<string, string> = {
  hobby: "HOBBY",
  plus: "PLUS",
  pro: "PRO",
  site: "SITE",
};

const TIER_COLORS: Record<string, string> = {
  hobby: "blue",
  plus: "purple",
  pro: "gold",
  site: "volcano",
};

interface TierBadgeProps {
  /** The lowest tier that grants the feature. */
  tier: PlanTierV3;
  /** What the badge is about, used in the tooltip ("RAW capture", "4K video"). */
  feature?: string;
}

export function TierBadge({ tier, feature }: TierBadgeProps) {
  const navigate = useNavigate();
  const label = TIER_LABELS[tier] || tier.toUpperCase();
  const planName = label.charAt(0) + label.slice(1).toLowerCase();

  return (
    <Tooltip
      title={
        feature
          ? `${feature} is available from the ${planName} plan`
          : `Available from the ${planName} plan`
      }
    >
      <Tag
        color={TIER_COLORS[tier] || "default"}
        style={{ cursor: "pointer", marginInlineEnd: 0, fontSize: 10, lineHeight: "16px" }}
        onClick={(e) => {
          e.stopPropagation();
          navigate("/account");
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
}

/**
 * A control that the plan switches off, with its badge alongside.
 *
 * The <span> wrapper is required: Ant Design does not forward mouse events
 * from a disabled control, so without it the tooltip never fires.
 */
export function GatedControl({
  locked,
  tier,
  feature,
  children,
}: {
  locked: boolean;
  tier: PlanTierV3;
  feature?: string;
  children: React.ReactNode;
}) {
  if (!locked) return <>{children}</>;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span>{children}</span>
      <TierBadge tier={tier} feature={feature} />
    </span>
  );
}
